import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { Link } from "react-router-dom";
import {
    Wheat, Activity, HeartPulse, Sprout, ShieldCheck,
    Flame, Droplet, Zap, Salad, ArrowRight,
} from "lucide-react";

const HEALTH_BENEFITS = [
    { icon: HeartPulse, title: "Heart-friendly", desc: "Low GI millets support healthy blood sugar, cholesterol and cardiovascular wellness." },
    { icon: Activity, title: "Sustained energy", desc: "Complex carbs release slowly — no crashes, no cravings, just steady fuel." },
    { icon: Sprout, title: "Gut-friendly", desc: "High-fibre grains promote digestion, gut health and satiety in every meal." },
    { icon: ShieldCheck, title: "Naturally gluten-friendly", desc: "Ancient millets suit gluten-sensitive diets without sacrificing taste or texture." },
];

const MACROS = [
    { icon: Zap, label: "Protein", value: "22–35g", desc: "Per meal — from paneer, tofu, chicken, tempeh and legumes." },
    { icon: Wheat, label: "Complex Carbs", value: "40–55g", desc: "Slow-release energy from foxtail, ragi, jowar and kodo millets." },
    { icon: Droplet, label: "Healthy Fats", value: "10–18g", desc: "Cold-pressed oils, seeds, nuts and avocado — never seed oils." },
    { icon: Salad, label: "Fibre", value: "8–14g", desc: "From whole millets, veggies and superfood toppings." },
    { icon: Flame, label: "Calories", value: "≤ 500", desc: "Every meal capped for portion-controlled nutrition." },
];

const PILLARS = [
    { title: "Protein First", desc: "Every meal delivers a minimum of 22g clean protein — engineered for muscle, satiety and recovery." },
    { title: "Millet Powered", desc: "100% ancient Indian grains: foxtail, ragi, jowar, bajra and kodo. Better for you, better for the planet." },
    { title: "No Empty Calories", desc: "We cap every meal at 500 kcal and pack it with real macros — no filler, no maida, no seed oils." },
    { title: "Whole & Recognisable", desc: "If you can't pronounce it, we don't cook with it. Simple, clean ingredients — always." },
];

const NutritionPage = () => (
    <div data-testid="nutrition-page" className="bg-pm-cream min-h-screen">
        <Header />

        {/* Hero */}
        <section className="pt-10 md:pt-16 pb-10 md:pb-14">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">Nutrition</span>
                <h1 data-testid="nutrition-headline" className="text-4xl md:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                    Real macros. <span className="text-pm-green">Real transparency.</span>
                </h1>
                <p className="mt-4 text-pm-black/70 max-w-2xl mx-auto text-base md:text-lg">
                    Every ingredient we use has a reason. Every gram we put in a bowl is measured. This is
                    the ProMill nutrition philosophy — clean food that fuels a modern Indian body.
                </p>
            </div>
        </section>

        {/* Philosophy */}
        <section data-testid="philosophy-section" className="bg-white py-16 md:py-20 border-y border-pm-black/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mb-10">
                    <span className="text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px]">Our Philosophy</span>
                    <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Four principles. <span className="text-pm-green">Zero compromise.</span>
                    </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    {PILLARS.map((p, i) => (
                        <div key={i} data-testid={`philosophy-${i}`} className="p-6 rounded-2xl border border-pm-black/8 bg-pm-cream card-lift">
                            <div className="text-4xl font-extrabold text-pm-green">0{i + 1}</div>
                            <h3 className="mt-2 text-xl font-extrabold text-pm-black">{p.title}</h3>
                            <p className="mt-2 text-sm text-pm-black/65 leading-relaxed">{p.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Health Benefits */}
        <section data-testid="benefits-section" className="py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px]">Health Benefits</span>
                    <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Why millet-based <span className="text-pm-green">wins.</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {HEALTH_BENEFITS.map((b, i) => (
                        <div key={i} data-testid={`benefit-${i}`} className="bg-white rounded-2xl p-6 border border-pm-black/8 flex flex-col items-center text-center card-lift">
                            <div className="h-14 w-14 rounded-full bg-pm-yellow/20 flex items-center justify-center mb-4">
                                <b.icon className="w-6 h-6 text-pm-green" strokeWidth={1.7} />
                            </div>
                            <h3 className="text-base font-extrabold text-pm-black">{b.title}</h3>
                            <p className="mt-2 text-sm text-pm-black/65 leading-relaxed">{b.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Macro Overview */}
        <section data-testid="macros-section" className="bg-pm-green text-white py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <span className="text-pm-yellow font-bold tracking-[0.25em] uppercase text-[11px]">Macro Overview</span>
                    <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-[-0.02em] leading-tight">
                        The <span className="text-pm-yellow">ProMill</span> plate.
                    </h2>
                    <p className="mt-3 text-white/80">Typical macro range across our menu — always per meal, always transparent.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                    {MACROS.map((m, i) => (
                        <div key={i} data-testid={`macro-${i}`} className="rounded-2xl border-2 border-pm-yellow/30 p-5 text-center hover:border-pm-yellow transition-colors">
                            <m.icon className="w-6 h-6 mx-auto mb-3 text-pm-yellow" />
                            <div className="text-xs uppercase tracking-widest text-white/70 font-bold">{m.label}</div>
                            <div className="mt-1 text-3xl font-extrabold text-pm-yellow">{m.value}</div>
                            <div className="mt-2 text-xs text-white/70 leading-relaxed">{m.desc}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Nutritional Information (transparency notes) */}
        <section data-testid="info-section" className="py-16 md:py-20 bg-pm-cream">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-10">
                    <span className="text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px]">Nutritional Information</span>
                    <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Nothing to hide. <span className="text-pm-green">Everything to prove.</span>
                    </h2>
                </div>
                <div className="bg-white rounded-2xl border border-pm-black/8 p-6 md:p-8 space-y-5 text-pm-black/75 leading-relaxed">
                    <p>Every ProMill product carries complete nutrition labelling — protein, carbs, fibre, fats, sugars, sodium and calorie count — visible before you order.</p>
                    <p><span className="font-bold text-pm-black">Allergens</span> are called out clearly on every meal. Common tags include gluten-free, dairy-free, nut-free and soy-free.</p>
                    <p><span className="font-bold text-pm-black">Sourcing:</span> All millets are stone-milled and sourced directly from farmer partners across Karnataka, Andhra Pradesh and Rajasthan.</p>
                    <p><span className="font-bold text-pm-black">Nothing we won't tell you:</span> zero refined sugar, zero seed oils (only cold-pressed groundnut, mustard, coconut and olive), zero artificial colours or preservatives.</p>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <Link to="/menu" data-testid="nutrition-menu-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pm-green text-pm-yellow font-bold hover:bg-pm-black transition-colors">
                        See Menu Macros <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/contact" data-testid="nutrition-contact-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-pm-black/15 font-bold hover:bg-white">
                        Ask a Nutritionist
                    </Link>
                </div>
            </div>
        </section>

        <Footer />
    </div>
);

export default NutritionPage;
